import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Badge, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import { customerSupportAPI } from '../../services/api';

interface Message {
  id: number;
  body: string;
  is_admin_reply: boolean;
  sender_name: string;
  created_at: string;
}

interface TicketDetailData {
  id: number;
  ticket_id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  messages: Message[];
  message_count: number;
  company_name: string;
  created_at: string;
  updated_at: string;
}

const statusColors: Record<string, { bg: string; text: string }> = {
  open: { bg: '#dbeafe', text: '#1e40af' },
  in_progress: { bg: '#fef3c7', text: '#92400e' },
  waiting: { bg: '#fce7f3', text: '#9d174d' },
  resolved: { bg: '#d1fae5', text: '#065f46' },
  closed: { bg: '#f3f4f6', text: '#6b7280' },
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

const categoryLabels: Record<string, string> = {
  general: 'General Question',
  billing: 'Billing & Payments',
  technical: 'Technical Issue',
  account: 'Account & Settings',
  feature: 'Feature Request',
  bug: 'Bug Report',
  other: 'Other',
};

const CustomerTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadTicket = useCallback(async () => {
    try {
      const res = await customerSupportAPI.getTicket(Number(id));
      setTicket(res.data);
    } catch {
      setError('Ticket not found.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await customerSupportAPI.reply(Number(id), replyBody.trim());
      setReplyBody('');
      await loadTicket();
    } catch {
      setError('Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    try {
      await customerSupportAPI.closeTicket(Number(id));
      await loadTicket();
    } catch {
      setError('Failed to close ticket.');
    }
  };

  const handleReopen = async () => {
    try {
      await customerSupportAPI.reopenTicket(Number(id));
      await loadTicket();
    } catch {
      setError('Failed to reopen ticket.');
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit',
    });
  };

  const isClosed = ticket?.status === 'closed' || ticket?.status === 'resolved';

  if (loading) {
    return (
      <>
        <Header />
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      </>
    );
  }

  if (!ticket) {
    return (
      <>
        <Header />
        <Container className="py-5 text-center">
          <ExclamationCircleIcon style={{ width: 48, height: 48, color: '#dc2626', marginBottom: 12 }} />
          <p style={{ color: '#64748b' }}>Ticket not found.</p>
          <Button variant="outline-secondary" onClick={() => navigate('/support')}>Back to Tickets</Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container fluid className="px-3 px-lg-4 page-content-padding" style={{ paddingTop: '24px' }}>
        {/* Back + Header */}
        <Button
          variant="link"
          className="d-flex align-items-center gap-1 p-0 mb-3"
          style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 500 }}
          onClick={() => navigate('/support')}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to tickets
        </Button>

        <Row className="g-4">
          {/* Main conversation area */}
          <Col lg={8}>
            {/* Subject header */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <div>
                  <h5 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>{ticket.subject}</h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{
                      background: statusColors[ticket.status]?.bg,
                      color: statusColors[ticket.status]?.text,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      padding: '4px 10px',
                      borderRadius: '6px',
                    }}>
                      {statusLabels[ticket.status]}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{ticket.ticket_id}</span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>•</span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{categoryLabels[ticket.category]}</span>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {isClosed ? (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="d-flex align-items-center gap-1"
                      style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}
                      onClick={handleReopen}
                    >
                      <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
                      Reopen
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="d-flex align-items-center gap-1"
                      style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}
                      onClick={handleClose}
                    >
                      <LockClosedIcon style={{ width: '14px', height: '14px' }} />
                      Close
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {error && <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>{error}</Alert>}

            {/* Messages */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}>
              {ticket.messages.map((msg, idx) => (
                <div
                  key={msg.id}
                  style={{
                    padding: '20px',
                    borderBottom: idx < ticket.messages.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: msg.is_admin_reply ? '#eff6ff' : 'transparent',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: msg.is_admin_reply
                        ? 'linear-gradient(135deg, #065f46, #059669)'
                        : 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 700, fontSize: '0.75rem',
                      flexShrink: 0,
                    }}>
                      {msg.is_admin_reply ? 'S' : msg.sender_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
                        {msg.sender_name}
                      </span>
                      {msg.is_admin_reply && (
                        <span style={{
                          background: '#d1fae5',
                          color: '#065f46',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          marginLeft: '6px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                        }}>
                          Staff
                        </span>
                      )}
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                        {formatDate(msg.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    paddingLeft: '44px',
                    fontSize: '0.9rem',
                    color: '#374151',
                    lineHeight: 1.65,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {msg.body}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply form */}
            {!isClosed ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '20px',
                marginTop: '16px',
              }}>
                <Form onSubmit={handleReply}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                      <ChatBubbleLeftRightIcon style={{ width: '16px', height: '16px', marginRight: '6px', color: '#1e3a8a' }} />
                      Reply
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={replyBody}
                      onChange={e => setReplyBody(e.target.value)}
                      placeholder="Type your message..."
                      required
                      style={{ borderColor: '#e2e8f0', borderRadius: '10px', fontSize: '0.9rem', resize: 'vertical' }}
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      disabled={sending || !replyBody.trim()}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: '10px',
                        padding: '10px 24px',
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                        color: 'white',
                        border: 'none',
                      }}
                    >
                      <PaperAirplaneIcon style={{ width: '16px', height: '16px' }} />
                      {sending ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </div>
                </Form>
              </div>
            ) : (
              <div style={{
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '20px',
                marginTop: '16px',
                textAlign: 'center',
              }}>
                <LockClosedIcon style={{ width: '20px', height: '20px', color: '#94a3b8', marginBottom: '6px' }} />
                <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '8px' }}>
                  This ticket is {ticket.status}. Reopen it to reply.
                </p>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleReopen}
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  <ArrowPathIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  Reopen Ticket
                </Button>
              </div>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
            }}>
              <h6 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '16px', fontSize: '0.92rem' }}>
                Ticket Details
              </h6>
              {[
                { label: 'Ticket ID', value: ticket.ticket_id },
                { label: 'Status', value: statusLabels[ticket.status], badge: true, badgeColor: statusColors[ticket.status] },
                { label: 'Priority', value: ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) },
                { label: 'Category', value: categoryLabels[ticket.category] },
                { label: 'Created', value: formatDate(ticket.created_at) },
                { label: 'Last Updated', value: formatDate(ticket.updated_at) },
                { label: 'Messages', value: String(ticket.message_count) },
              ].map((item, i) => (
                <div key={item.label} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: i < 6 ? '1px solid #f1f5f9' : 'none',
                }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{item.label}</span>
                  {item.badge ? (
                    <span style={{
                      background: item.badgeColor?.bg,
                      color: item.badgeColor?.text,
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      padding: '3px 8px',
                      borderRadius: '6px',
                    }}>
                      {item.value}
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 500 }}>{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CustomerTicketDetail;
